"use client";

import { format } from "date-fns";
import { HardDriveIcon } from "lucide-react";
import { Dispatch, SetStateAction, useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from "@/components/ui/table";
import { PublicRecording } from "@classview/shared-types";
import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { RecordingStorageActions } from "./recording-storage-actions";

interface RecordingStorageTableProps {
  data: PublicRecording[];
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pageCount?: number;
  isLoading?: boolean;
}

export function AdminRecordingTable({
  data,
  pagination,
  onPaginationChange,
  pageCount,
  isLoading,
}: RecordingStorageTableProps) {
  const columnHelper = createColumnHelper<PublicRecording>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => (
          <div className="flex flex-col">
            <span className="font-medium">{info.getValue()}</span>
          </div>
        ),
        header: () => <span>Recording Name</span>,
      }),
      columnHelper.accessor("daysLeftToDelete", {
        cell: (info) => {
          const timeLeftToDelete = info.getValue();
          const isPermanent = info.row.original.isPermanent;

          if (isPermanent) {
            return <Badge variant="outline">Permanent</Badge>;
          }

          if (timeLeftToDelete && timeLeftToDelete > 0) {
            return <Badge variant="outline">{timeLeftToDelete} days</Badge>;
          }

          return (
            <Badge variant="outline">
              <div className="mr-2 h-2 w-2 rounded-full bg-destructive" />{" "}
              Expired
            </Badge>
          );
        },
        header: () => <span>Time until Deletion</span>,
        size: 120,
      }),
      columnHelper.accessor("createdAt", {
        cell: (info) => format(new Date(info.getValue()), "dd/MM/yyyy"),
        header: () => <span>Recording Date</span>,
        size: 120,
      }),

      columnHelper.accessor("organisation.name", {
        cell: (info) => info.getValue(),
        header: () => <span>Organiser</span>,
        size: 130,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => (
          <RecordingStorageActions
            recordingId={info.row.original.id}
            recordingName={info.row.original.name}
          />
        ),
        size: 100,
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
      },
    },
    state: {
      pagination,
    },
    defaultColumn: {
      size: 999,
    },
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="hover:bg-transparent" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize() === 999 ? "auto" : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              <p className="mb-2 flex flex-col items-center justify-center">
                <HardDriveIcon className="size-6 text-secondary" />
              </p>
              <p className="font-semibold text-foreground">
                No recordings found
              </p>
              <p className="text-muted">
                Please refine your search or try a different one
              </p>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TablePagination table={table} isLoading={isLoading} />
      </TableFooter>
    </Table>
  );
}
